import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import styled, { css } from "styled-components";
import { useDebouncedCode } from "../../../hook/Subscription/useDebouncedCode";
import { useValidateReferral } from "../../../hook/Subscription/useValidateReferral";

/**
 * Referral code input — same UX as PromoBox without discount badge.
 * Doesn't display referrer name (UX decision: code-only feedback).
 */
function ReferralBox({ initialCode = "", onChange, lang = "th" }) {
  const [input, setInput] = useState(initialCode);
  const debounced = useDebouncedCode(input, 500);

  const { data, isLoading, error } = useValidateReferral({ code: debounced });

  useEffect(() => {
    if (data) onChange?.(data);
    else if (!input) onChange?.(null);
  }, [data, input, onChange]);

  const handleClear = () => {
    setInput("");
    onChange?.(null);
  };

  const isEmpty = !input;
  const isValid = !!data && !isLoading;
  const isInvalid = !!error && !isLoading && !!debounced;

  return (
    <Wrapper>
      <SectionLabel>{lang === "en" ? "REFERRAL" : "รหัสแนะนำ"}</SectionLabel>

      <Field $valid={isValid} $invalid={isInvalid}>
        {isValid && <ValidIcon aria-hidden>✓</ValidIcon>}

        <Input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={lang === "en" ? "Optional" : "ไม่บังคับ"}
          readOnly={isValid}
          aria-invalid={isInvalid}
          $bold={isValid}
        />

        {isLoading && <Spinner animation="border" size="sm" variant="secondary" />}

        {!isEmpty && !isLoading && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label={lang === "en" ? "Clear" : "ลบ"}
          >
            ✕
          </ClearButton>
        )}
      </Field>

      {isInvalid && (
        <ErrorText>
          {error?.message || (lang === "en" ? "Invalid code" : "รหัสไม่ถูกต้อง")}
        </ErrorText>
      )}
    </Wrapper>
  );
}

export default ReferralBox;

// ─── Styled ──────────────────────────────────

const Wrapper = styled.div`
  padding: 0 16px 8px;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  transition: all 0.15s;

  ${({ $valid }) =>
    $valid &&
    css`
      border-color: #10b981;
      background: rgba(16, 185, 129, 0.08);
    `}

  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: #ef4444;
    `}

  &:focus-within {
    border-color: #6366f1;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: #111827;
  padding: 0;
  height: 100%;

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:read-only {
    cursor: default;
  }
`;

const ValidIcon = styled.span`
  color: #10b981;
  font-weight: 700;
  margin-right: 6px;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 4px;
  margin-left: 4px;

  &:hover {
    color: #6b7280;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 4px;
`;